
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { useReferralTerms } from "@/hooks/use-referral-terms";
import { AlertTriangle } from "lucide-react";

interface ReferralTermsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ReferralTermsDialog = ({ isOpen, onClose }: ReferralTermsDialogProps) => {
  const [hasRead, setHasRead] = useState(false);
  const { activeTerms, agreeToTerms } = useReferralTerms();

  const handleAgree = async () => {
    if (!activeTerms) return;
    
    try {
      await agreeToTerms.mutateAsync(activeTerms.version);
      onClose();
    } catch (error) {
      console.error('Failed to agree to terms:', error);
    }
  };

  const handleCheckboxChange = (checked: boolean | "indeterminate") => {
    setHasRead(checked === true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Referral Program Terms and Conditions
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-96 pr-4">
          <div className="text-sm text-gray-700 whitespace-pre-line">
            {activeTerms?.content || 'Loading terms...'}
          </div>
        </ScrollArea>

        <div className="flex items-center space-x-2">
          <Checkbox 
            id="terms-read" 
            checked={hasRead}
            onCheckedChange={handleCheckboxChange}
          />
          <label htmlFor="terms-read" className="text-sm">
            I have read and understand the terms and conditions
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleAgree}
            disabled={!hasRead || agreeToTerms.isPending}
          >
            {agreeToTerms.isPending ? 'Processing...' : 'I Agree'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
